import React, {useState, useEffect} from "react";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import ModalBase from "../../../../connectui/component/modal/ModalBase";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Container from "../../../../connectui/component/layout/Container";
import RoundIcon from "../../../../connectui/component/display/RoundIcon";
import MergeGuestConfirmationDialog from "./MergeGuestConfirmationDialog";
import MergeGuestConflictsDialog from "./MergeGuestConflictsDialog";

const useStyles = (theme) =>
  buildStyles({
    modalContent: {
      width: "700px",
      minHeight: "668px",
      maxWidth: "90vw",
      maxHeight: "90vh",
      padding: theme.spacing(6),
      display: "flex",
      flexDirection: "column",
    },
    header: {
      marginBottom: theme.spacing(4),
    },
  });

const TOTAL_PAGES = 3;

const MergeGuestModal = ({
  isOpen,
  onClose,
  mergeConflicts,
  onMerge,
  primaryMergeCandidate,
  secondaryMergeCandidate,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFields, setSelectedFields] = useState({});
  const [selectedDisplayValues, setSelectedDisplayValues] = useState({});

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      // Pre-select all primary values
      const initialSelectedFields = {};
      const initialSelectedDisplayValues = {};

      // Pre-select all conflicts with primary values
      ["guest", "booking", "listing"].forEach((type) => {
        if (mergeConflicts?.[type]?.conflicts) {
          mergeConflicts[type].conflicts.forEach((conflict) => {
            const fieldKey = `${type}.${conflict.field}`;
            initialSelectedFields[fieldKey] = conflict.primary_value;
            initialSelectedDisplayValues[fieldKey] = conflict.primary_display;
          });
        }
      });

      setSelectedFields(initialSelectedFields);
      setSelectedDisplayValues(initialSelectedDisplayValues);
    }
  }, [
    isOpen,
    mergeConflicts,
    setCurrentPage,
    setSelectedFields,
    setSelectedDisplayValues,
  ]);

  const conflictsData = mergeConflicts || {
    guest: {
      conflicts: [],
      auto_resolved_fields: [],
    },
    booking: {
      conflicts: [],
      auto_resolved_fields: [],
    },
    listing: {
      conflicts: [],
      auto_resolved_fields: [],
    },
  };

  const getCurrentPageData = () => {
    const createConflictItem = (type, conflict) => ({
      field: `${type}.${conflict.field}`,
      label: conflict.field
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      primary: {
        value: conflict.primary_value,
        display: conflict.primary_display,
      },
      secondary: {
        value: conflict.secondary_value,
        display: conflict.secondary_display,
      },
      isAutoResolved: false,
    });

    const createAutoResolvedItem = (type, field) => ({
      field: `${type}.${field.field}`,
      label: field.field
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      primary: {
        value: field.primary_value,
        display: field.primary_display,
      },
      secondary: {
        value: field.secondary_value,
        display: field.secondary_display,
      },
      isAutoResolved: true,
      autoResolvedValue: field.display_value,
    });

    const mapConflicts = (type) =>
      conflictsData[type].conflicts.map((conflict) =>
        createConflictItem(type, conflict),
      );

    const mapAutoResolved = (type) =>
      conflictsData[type].auto_resolved_fields.map((field) =>
        createAutoResolvedItem(type, field),
      );

    switch (currentPage) {
      case 1:
        return [...mapConflicts("guest"), ...mapAutoResolved("guest")];

      case 2:
        return [
          ...mapConflicts("booking"),
          ...mapAutoResolved("booking"),
          ...mapConflicts("listing"),
          ...mapAutoResolved("listing"),
        ];

      default:
        return [];
    }
  };

  const pageData = getCurrentPageData();

  const handleBack = () => {
    if (currentPage === 1) {
      onClose();
    } else {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleContinue = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handleSelectField = (fieldName, value, displayValue) => {
    setSelectedFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    setSelectedDisplayValues((prev) => ({
      ...prev,
      [fieldName]: displayValue,
    }));
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} sx={{minHeight: "668px"}}>
      <Container className={classes.modalContent}>
        <Container className={classes.header}>
          <FlexContainer alignItems="center" gap={theme.spacing(2)}>
            <RoundIcon iconComponent="merge" baseColor="primary" />
          </FlexContainer>
        </Container>
        {currentPage === TOTAL_PAGES ? (
          <MergeGuestConfirmationDialog
            conflictsData={conflictsData}
            selectedDisplayValues={selectedDisplayValues}
            onBack={handleBack}
            onMerge={onMerge}
            primaryMergeCandidate={primaryMergeCandidate}
            secondaryMergeCandidate={secondaryMergeCandidate}
          />
        ) : (
          <MergeGuestConflictsDialog
            currentPage={currentPage}
            pageData={pageData}
            selectedFields={selectedFields}
            onSelectField={handleSelectField}
            onBack={handleBack}
            onContinue={handleContinue}
          />
        )}
      </Container>
    </ModalBase>
  );
};

export default MergeGuestModal;
